// formkit.config.ts
import { sk } from '@formkit/i18n'
import { DefaultConfigOptions } from '@formkit/vue'
import { createProPlugin, mask } from '@formkit/pro'
import { createFloatingLabelsPlugin } from '@formkit/addons'
import '@formkit/addons/css/floatingLabels'

const config: DefaultConfigOptions = {
    locales: { sk },
    locale: 'sk',
}

/**
 * Accessibility plugin: Announces validation errors to screen readers (WCAG 3.3.1, 4.1.3)
 * Sets aria-invalid on invalid fields and role/aria-live on the messages list.
 *
 * Note: aria-describedby is already wired by FormKit core via the reactive
 * $describedBy context value (see @formkit/vue bindings) — it points at every
 * visible message id (`${id}-${messageKey}`) and updates as messages appear or
 * disappear. We must NOT override it here, otherwise we replace a working
 * reactive value with a stale/incorrect target.
 */
/* @ts-expect-error */
function accessibilityPlugin(node) {
    node.on('created', () => {
        // Skip non-input nodes
        if (!node.props.definition?.schema) return

        const schemaFn = node.props.definition.schema
        node.props.definition.schemaMemoKey = `a11y_${node.props.definition.schemaMemoKey}`

        node.props.definition.schema = (sectionsSchema = {}) => {
            /* @ts-expect-error */
            sectionsSchema.input = {
                attrs: {
                    'aria-invalid': '$state.valid === false && $state.validationVisible ? "true" : undefined',
                },
            }

            /* @ts-expect-error */
            sectionsSchema.messages = {
                attrs: {
                    role: '$state.valid === false && $state.validationVisible ? "alert" : undefined',
                    'aria-live': '$state.valid === false && $state.validationVisible ? "assertive" : "polite"',
                },
            }

            return schemaFn(sectionsSchema)
        }
    })
}

const pro = createProPlugin('fk-891ac26c124', { mask })

/* @ts-expect-error */
function isCheckboxAndRadioMultiple(node) {
    return (
        (node.props.type === 'checkbox' || node.props.type === 'radio') &&
        node.props.options
    )
}

/* @ts-expect-error */
function addAsteriskPlugin(node) {
    node.on('created', () => {
        const isRequired = node.props.parsedRules.some(
            (rule: { name: string }) => ['required', 'accepted'].includes(rule.name)
        )
        if (!isRequired) return

        const isMultiOption = isCheckboxAndRadioMultiple(node)

        // if we're going to modify the schema then we need
        // to update the schemaMemoKey so we don't get an
        // invalid cached schema.
        node.props.definition.schemaMemoKey = `required_${
            isMultiOption ? 'multi_' : ''
        }${node.props.definition.schemaMemoKey}`

        const schemaFn = node.props.definition.schema
        node.props.definition.schema = (sectionsSchema = {}) => {
            if (isRequired) {
                if (isMultiOption) {
                    /* @ts-expect-error */
                    sectionsSchema.legend = {
                        children: ['$label', {
                            $el: 'span',
                            if: '$state.required',
                            attrs: {
                              'aria-label': 'povinné pole',
                            },
                            children: ['*']
                          }],
                    }
                } else {
                    /* @ts-expect-error */
                    sectionsSchema.label = {
                        children: ['$label', {
                            $el: 'span',
                            if: '$state.required',
                            attrs: {
                              'aria-label': 'povinné pole',
                            },
                            children: ['*']
                          }],
                    }
                }
            }
            return schemaFn(sectionsSchema)
        }
    })
}

export default {
    plugins: [pro, addAsteriskPlugin, accessibilityPlugin, createFloatingLabelsPlugin({
        useAsDefault: true,
    })],
    ...config,
}
